import { ChartTable, LegendSummaryTableProps } from "../table/SummaryTable";
import { normalizeDisplayLabel } from "../../utility/displayText";

export default function AgentTable({ tableData }: { tableData: any }) {
  // Validate tableData before processing
  if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
    return (
      <div className="w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#34656d]/5 to-transparent">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-[#34656d]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 3v18h18" />
              <path d="M3 9h18" />
              <path d="M3 15h18" />
            </svg>
            <h3 className="text-sm font-semibold text-[#34656d]">Data Table</h3>
          </div>
        </div>
        <div className="px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">No table data available</p>
        </div>
      </div>
    );
  }

  // Check if first row exists and is an object
  if (
    !tableData[0] ||
    typeof tableData[0] !== "object" ||
    tableData[0] === null
  ) {
    return (
      <div className="w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#34656d]/5 to-transparent">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-[#34656d]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 3v18h18" />
              <path d="M3 9h18" />
              <path d="M3 15h18" />
            </svg>
            <h3 className="text-sm font-semibold text-[#34656d]">Data Table</h3>
          </div>
        </div>
        <div className="px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">Invalid table data format</p>
        </div>
      </div>
    );
  }

  const keys = Object.keys(tableData[0]);
  const tablePar: LegendSummaryTableProps = {
    legends: keys.map((key) => {
      let name = normalizeDisplayLabel(key).replace(/\(\)/g, "");
      // Remove duplicate consecutive words while preserving spaces between different words
      name = name.replace(/\b(\w+)\s+\1\b/g, "$1").replace("()", "");
      return {
        dataKey: key,
        name: name,
        unit: "",
      };
    }),
    data: tableData,
    extraKeys: [],
    extraLabels: [],
  };

  const downloadTableCSV = (tableData: any) => {
    try {
      // Validate data before processing
      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        console.error("Invalid table data for CSV download");
        return;
      }

      // Implementation for downloading table data as CSV
      console.log("Downloading table data as CSV", tableData);
      const csvContent = tableData
        .map((row: any) => {
          if (!row || typeof row !== "object") {
            return "";
          }
          return Object.values(row).join(",");
        })
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "table-data.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading table as CSV:", error);
    }
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#34656d]/5 to-transparent chart-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-[#34656d]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 3v18h18" />
              <path d="M3 9h18" />
              <path d="M3 15h18" />
            </svg>
            <h3 className="text-sm font-semibold text-[#34656d] chart-title">
              Data Table
            </h3>
          </div>
          <button
            onClick={() => downloadTableCSV(tableData)}
            className="px-4 py-2 text-sm font-medium text-[#34656d] hover:bg-[#34656d]/5 rounded-lg transition-colors duration-200 flex items-center space-x-2 download-button"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Download Data</span>
          </button>
        </div>
      </div>

      <div className="px-6 gas-chart-container pt-4 bg-white">
        <div className="flex flex-col h-full overflow-auto custom-scrollbar h-full">
          <ChartTable {...tablePar} />
        </div>
      </div>
    </div>
  );
}
